import type { ChangeSetId, DeploymentId, EnquiryId, EnquiryMeasurementId, SiteId } from "./ids.js";
import type { EvidenceConfidence, MeasurementVerdict } from "./continuous-improvement.js";

/**
 * Enquiries: what a visitor sends through the contact form of a customer's site (ADR-0028).
 *
 * A static site has no server of its own, so its form posts to SiteAgain. SiteAgain keeps the message
 * for the site's owner, emails it to them, and counts it, so that a published change can be compared
 * with how many people got in touch before and after. The counts are observations; none of this says
 * a change caused anything.
 *
 * Not to be confused with `AssessmentLeadId`, which is a prospect who unlocked a free assessment.
 */

/** Personal data in an enquiry is erased after this long. The anonymous row (day, page, channel) is deleted with it. */
export const ENQUIRY_RETENTION_DAYS = 730;
/** Fields kept per enquiry, characters kept per field, and bytes read from one request. */
export const ENQUIRY_MAX_FIELDS = 40;
export const ENQUIRY_MAX_VALUE_CHARS = 4_000;
export const ENQUIRY_MAX_BODY_BYTES = 32_768;
/** An enquiry is measured over this many days before and after a change: fewer would be noise for a small site. */
export const ENQUIRY_MEASURE_WINDOW_DAYS = 28;

/**
 * new: arrived, nobody has said anything about it yet.
 * qualified: a real prospect. won: became a customer. lost: did not.
 * spam: not a person. Spam and test enquiries are never counted.
 */
export type EnquiryStatus = "new" | "qualified" | "won" | "lost" | "spam";
export const ENQUIRY_STATUSES: readonly EnquiryStatus[] = ["new", "qualified", "won", "lost", "spam"];

/** Where the visitor came from, when the page could tell. `unknown` means it could not, never "direct". */
export type EnquiryChannel = "search" | "social" | "referral" | "direct" | "unknown";

/** Whether the owner has been emailed about it. */
export type EnquiryNotifyStatus = "pending" | "sent" | "skipped" | "failed";

/** An enquiry as its owner reads it. */
export interface EnquiryView {
  id: EnquiryId;
  receivedAt: string;
  status: EnquiryStatus;
  /** Sent from the dashboard to try the path. Never counted. */
  isTest: boolean;
  name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
  /** Everything else the form sent, as the visitor typed it. */
  fields: Record<string, string>;
  /** The page the form is on. */
  pagePath: string | null;
  /** The first page the visitor saw in this visit, when the site said so. */
  landingPath: string | null;
  channel: EnquiryChannel;
  referrerHost: string | null;
  utmSource: string | null;
  notify: { status: EnquiryNotifyStatus; detail: string | null };
  /** Personal data was erased on request or after retention. Name, email, phone, message and fields are empty. */
  erasedAt: string | null;
}

export interface EnquiryTotals {
  /** Every enquiry that was not spam and not a test. */
  total: number;
  /** Enquiries judged real prospects by their owner. Includes `won`. */
  qualified: number;
  won: number;
  /** Enquiries whose visit began on a search engine. Only meaningful when `attributed` is most of `total`. */
  fromSearch: number;
  /** Enquiries whose channel is known. */
  attributed: number;
}

export interface EnquiryDay {
  /** UTC `YYYY-MM-DD`. */
  date: string;
  count: number;
}

export interface EnquiryRow {
  key: string;
  count: number;
}

/** What the owner sees about enquiries on one site. */
export interface EnquiriesView {
  siteId: SiteId;
  /**
   * Where a form posts to. Null when the app cannot say where it is served from, in which case the screen
   * says setup is not available yet rather than showing an address that goes nowhere.
   */
  endpointUrl: string | null;
  /** The first moment anything reached the endpoint (a test counts). Before this, "no enquiries" means "not listening". */
  collectingSince: string | null;
  /** Whether the owner has an address to be emailed at. */
  canNotify: boolean;
  /** The window the figures below describe, ending today. Null before collection starts. */
  window: { from: string; to: string; days: number } | null;
  totals: EnquiryTotals | null;
  /** The same number of days before the window. Null when collection started inside it. */
  previousTotals: EnquiryTotals | null;
  series: EnquiryDay[];
  byPage: EnquiryRow[];
  byChannel: EnquiryRow[];
  /** All-time counts per status, spam and tests excluded from `new` through `lost`. */
  statusCounts: Record<EnquiryStatus, number>;
  /** Newest first. */
  enquiries: EnquiryView[];
  hasMore: boolean;
  measurements: EnquiryMeasurementView[];
}

export type EnquiryInsufficientReason = "baseline_incomplete" | "window_incomplete" | "too_few_enquiries";

export interface EnquiryWindowTotals extends EnquiryTotals {
  from: string;
  to: string;
  days: number;
}

/**
 * The number of enquiries around one published change: the same number of days before and after, for the
 * whole site. With a small site that is a handful of messages, so most results are `insufficient` or
 * `neutral`, and a verdict needs both a large relative move and enough absolute messages to mean anything.
 * There is no rest-of-site control here, which is why confidence never reaches `high`.
 */
export interface EnquiryMeasurementV1 {
  id: EnquiryMeasurementId;
  siteId: SiteId;
  changeSetId: ChangeSetId;
  deploymentId: DeploymentId | null;
  windowDays: 7 | 14 | 28;
  /** The UTC day the change went live. It is in neither window. */
  deployedOn: string;
  before: EnquiryWindowTotals;
  after: EnquiryWindowTotals;
  change: {
    /** after minus before. */
    delta: number;
    /** Null when there were no enquiries before. */
    percent: number | null;
    /** after minus before for search-origin enquiries; null when most enquiries had no known channel. */
    searchDelta: number | null;
  };
  verdict: MeasurementVerdict;
  confidence: EvidenceConfidence;
  /** Set when the verdict is `insufficient`. */
  reason: EnquiryInsufficientReason | null;
  /** Other changes published inside the two windows, which makes this comparison weaker. */
  overlappingChanges: number;
  caveat: string;
  measuredAt: string;
}

/** A measurement as the owner reads it: with the plan's own summary of the change it is about. */
export type EnquiryMeasurementView = EnquiryMeasurementV1 & {
  changeSummary: string | null;
};

/** The slice of enquiries a monthly report freezes. */
export interface MonthlyEnquirySummary {
  /** Enquiries received in the month, spam and tests excluded. */
  total: number;
  /** The month before. Null when collection started after it began, so the comparison would be false. */
  previousMonth: number | null;
  qualified: number;
  won: number;
  /** Search-origin enquiries; null when most enquiries had no known channel. */
  fromSearch: number | null;
  topPages: EnquiryRow[];
  collectingSince: string;
  measurements: Array<Pick<
    EnquiryMeasurementV1,
    "changeSetId" | "windowDays" | "before" | "after" | "change" | "verdict" | "confidence" | "reason" | "measuredAt" | "caveat" | "overlappingChanges"
  >>;
}

/** What an operator may see of a site's enquiries: counts and delivery, never a visitor's words. */
export interface OperatorEnquirySummary {
  collectingSince: string | null;
  lastReceivedAt: string | null;
  last28Days: number;
  previous28Days: number | null;
  notifyFailures: number;
  notifySkipped: number;
  measurements: Array<Pick<EnquiryMeasurementV1, "changeSetId" | "windowDays" | "verdict" | "confidence" | "reason" | "measuredAt"> & {
    before: number;
    after: number;
  }>;
}
